import {Link} from "@inertiajs/react";
import {Activity, CreditCard, FileText, MapPin, Tag, ShieldAlert} from "lucide-react";

interface AdminStats {
  taxes_count: number
  tax_modules_count: number
  tax_types_count: number
  categories_count: number
  tax_payer_types_count: number
  areas_count: number
  tax_payers_count: number
  devices_active_count: number
  devices_inactive_count: number
  collect_sessions_count: number
  collected_amount_total: number
  database_connected: boolean
  active_agents_count: number
  total_agents_count: number
  payments_today_count: number
  collected_amount_today: number
}

interface IndexProps {
  stats: AdminStats
}

export default function Index({ stats }: IndexProps) {
  return (
      <div className="p-8">
          <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground">Administration système</h2>
              <p className="text-muted-foreground">Gérer les agents, les taxes et la configuration du système</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">

              <Link href="/admin/taxes">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-accent/10 p-3">
                              <FileText className="h-6 w-6 text-accent" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Les taxes</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Configurer les taxes et leurs règles de calcul</p>
                              <div className="mt-4 flex items-center gap-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.taxes_count}</span>
                                      <span className="ml-1 text-muted-foreground">Taxes</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>
              <Link href="/admin/tax_modules">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-accent/10 p-3">
                              <FileText className="h-6 w-6 text-accent" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Gestion des modules fiscaux</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Configurer les types de taxe, les taux et les règles de collecte</p>
                              <div className="mt-4 flex items-center gap-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.tax_modules_count}</span>
                                      <span className="ml-1 text-muted-foreground">Modules</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>
              <Link href="/admin/tax_types">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-accent/10 p-3">
                              <FileText className="h-6 w-6 text-accent" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Les types de taxe</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Configurer les différents types de taxe</p>
                              <div className="mt-4 flex items-center gap-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.tax_types_count}</span>
                                      <span className="ml-1 text-muted-foreground">Types</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>
              <Link href="/admin/categories">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-success/10 p-3">
                              <Tag className="h-6 w-6 text-success" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Gestion des catégories</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Gérer les catégories et les classifications de contribuables</p>
                              <div className="mt-4 flex items-center gap-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.categories_count}</span>
                                      <span className="ml-1 text-muted-foreground">Catégories</span>
                                  </div>
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.tax_payers_count}</span>
                                      <span className="ml-1 text-muted-foreground">Contribuables</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>
              <Link href="/admin/tax_payer_types">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-accent/10 p-3">
                              <FileText className="h-6 w-6 text-accent" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Les types de contribuable</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Gestion des différents types de contribuables</p>
                              <div className="mt-4 flex items-center gap-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.tax_payer_types_count}</span>
                                      <span className="ml-1 text-muted-foreground">Types</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>

              <Link href="/admin/areas">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-info/10 p-3">
                              <MapPin className="h-6 w-6 text-info" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Gestion des zones</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Gérer les zones de collecte et les affecter aux agents</p>
                              <div className="mt-4 flex items-center gap-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.areas_count}</span>
                                      <span className="ml-1 text-muted-foreground">Zones</span>
                                  </div>
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.tax_payers_count}</span>
                                      <span className="ml-1 text-muted-foreground">Contribuables</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>

              <Link href="/admin/devices">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-warning/10 p-3">
                              <CreditCard className="h-6 w-6 text-warning" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Les terminaux</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Gestion des terminaux POS et des transactions</p>
                              <div className="mt-4 flex items-center gap-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.devices_active_count}</span>
                                      <span className="ml-1 text-muted-foreground">Actifs</span>
                                  </div>
                                  <div>
                                      <span className="text-2xl font-bold text-muted-foreground">{stats.devices_inactive_count}</span>
                                      <span className="ml-1 text-muted-foreground">Inactifs</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>

              <Link href="/admin/collection">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-info/10 p-3">
                              <Activity className="h-6 w-6 text-info" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Sessions de collecte</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Gérer les sessions de collecte et les terminaux</p>
                              <div className="mt-4 flex items-center gap-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.collect_sessions_count}</span>
                                      <span className="ml-1 text-muted-foreground">Sessions</span>
                                  </div>
                                  <div>
                                      <span className="text-2xl font-bold text-success">{Number(stats.collected_amount_total || 0).toFixed(2)} FCFA</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>

              <Link href="/admin/financial_audits">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-destructive/10 p-3">
                              <ShieldAlert className="h-6 w-6 text-destructive" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Audit financier</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Vérifier les écarts entre paiements et montants déclarés.</p>
                              <div className="mt-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.collect_sessions_count}</span>
                                      <span className="ml-1 text-muted-foreground">Sessions à auditer</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>

              <Link href="/admin/payment_audits">
                  <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                      <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-warning/10 p-3">
                              <CreditCard className="h-6 w-6 text-warning" />
                          </div>
                          <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground">Audit des paiements</h3>
                              <p className="mt-1 text-sm text-muted-foreground">Analyser les transactions et détecter les anomalies de paiement.</p>
                              <div className="mt-4 text-sm">
                                  <div>
                                      <span className="text-2xl font-bold text-foreground">{stats.payments_today_count}</span>
                                      <span className="ml-1 text-muted-foreground">Paiements aujourd'hui</span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </Link>
          </div>

          <div className="mt-6 rounded-lg border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold text-foreground">Informations système</h3>
              <div className="grid gap-4 md:grid-cols-4">
                  <div className="rounded-lg border border-border bg-secondary p-4">
                      <p className="text-sm text-muted-foreground">État de la base de données</p>
                      <p className="mt-2 flex items-center gap-2 text-lg font-semibold text-success">
                          <span className={`h-2 w-2 rounded-full ${stats.database_connected ? "bg-success" : "bg-destructive"}`}></span>
                          {stats.database_connected ? "Connectée" : "Déconnectée"}
                      </p>
                  </div>
                  <div className="rounded-lg border border-border bg-secondary p-4">
                      <p className="text-sm text-muted-foreground">Agents actifs</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        {stats.active_agents_count} / {stats.total_agents_count}
                      </p>
                  </div>
                  <div className="rounded-lg border border-border bg-secondary p-4">
                      <p className="text-sm text-muted-foreground">Paiements aujourd'hui</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">{stats.payments_today_count}</p>
                  </div>
                  <div className="rounded-lg border border-border bg-secondary p-4">
                      <p className="text-sm text-muted-foreground">Collecte aujourd'hui</p>
                      <p className="mt-2 font-mono text-sm text-foreground">
                        {Number(stats.collected_amount_today || 0).toFixed(2)} FCFA
                      </p>
                  </div>
              </div>
          </div>
      </div>
  );
}
